import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { cn } from "../../lib/utils.js";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../../Components/ui/collapsible.js";
import { ChevronDown } from "lucide-react";
import { SearchQueryPill } from "./SearchQueryPill.js";
import { ChainOfThoughtWidget } from "@brandclub/bc-widgets";
const SearchIcon = ({ className, size = 14, color }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 14 14",
    fill: "none",
    className,
    style: color ? { color } : void 0,
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M5.88248 0C9.13129 0 11.765 2.63362 11.765 5.88234C11.765 7.22364 11.316 8.46008 10.5602 9.44961L13.1039 11.9944C13.4102 12.3007 13.4102 12.7973 13.1039 13.1036C12.8212 13.3863 12.3763 13.4081 12.0686 13.1689L11.9947 13.1036L9.44983 10.56C8.46027 11.3158 7.2238 11.7647 5.88248 11.7647C2.63367 11.7647 0 9.1311 0 5.88237C0 2.63364 2.63368 2.7351e-05 5.88248 2.7351e-05V0Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M5.8927 2.4707C7.777 2.47088 9.30481 3.99847 9.30481 5.88281C9.30463 7.767 7.77689 9.29474 5.8927 9.29492C4.00836 9.29492 2.48077 7.76711 2.48059 5.88281C2.48059 3.99836 4.00825 2.4707 5.8927 2.4707Z",
          className: "tw:fill-background tw:stroke-background",
          strokeWidth: "1.33333"
        }
      )
    ]
  }
);
const ProductSearchQueriesSectionInner = ({
  previewSearchQueries: previewSearchQueriesProp,
  previewQueryProducts,
  isStreaming,
  hasContent,
  isLastMessage = false,
  styles,
  resolveColor
}) => {
  const previewSearchQueries = Array.isArray(previewSearchQueriesProp) ? previewSearchQueriesProp : [];
  const [animatedQueryIndices, setAnimatedQueryIndices] = useState(/* @__PURE__ */ new Set());
  const [lastProcessedLength, setLastProcessedLength] = useState(0);
  const [hasEverStreamed, setHasEverStreamed] = useState(false);
  const [isOpen, setIsOpen] = useState(styles.defaultExpanded);
  const userHasInteracted = useRef(false);
  const handleOpenChange = (open) => {
    userHasInteracted.current = true;
    setIsOpen(open);
  };
  useEffect(() => {
    if (isStreaming) {
      setHasEverStreamed(true);
    }
  }, [isStreaming]);
  useEffect(() => {
    if (!isStreaming || !styles.animationsEnabled) {
      setAnimatedQueryIndices(/* @__PURE__ */ new Set());
      return;
    }
    if (previewQueryProducts.length > lastProcessedLength) {
      const newProducts = previewQueryProducts.slice(lastProcessedLength);
      newProducts.forEach((queryData) => {
        setAnimatedQueryIndices((prev) => /* @__PURE__ */ new Set([...prev, queryData.queryIndex]));
        setTimeout(() => {
          setAnimatedQueryIndices((prev) => {
            const next = new Set(prev);
            next.delete(queryData.queryIndex);
            return next;
          });
        }, styles.productAnimationDuration + 100);
      });
      setLastProcessedLength(previewQueryProducts.length);
    }
  }, [
    previewQueryProducts.length,
    isStreaming,
    lastProcessedLength,
    styles.animationsEnabled,
    styles.productAnimationDuration
  ]);
  const allQueriesHaveProducts = previewSearchQueries.length > 0 && previewSearchQueries.every((query) => previewQueryProducts.some((qp) => qp.query === query));
  useEffect(() => {
    if (styles.autoCollapse && isStreaming && isLastMessage && allQueriesHaveProducts && hasContent && !userHasInteracted.current) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, styles.autoCollapseDelay);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, isLastMessage, allQueriesHaveProducts, hasContent, styles.autoCollapse, styles.autoCollapseDelay]);
  useEffect(() => {
    if (!isStreaming && !hasEverStreamed && previewSearchQueries.length > 0) {
      setIsOpen(false);
    }
  }, [isStreaming, hasEverStreamed, previewSearchQueries.length]);
  const fontWeightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return /* @__PURE__ */ jsxs(
    Collapsible,
    {
      open: isOpen,
      onOpenChange: handleOpenChange,
      className: "tw:w-full",
      style: { marginBottom: `${styles.containerMarginBottom}px` },
      children: [
        /* @__PURE__ */ jsxs(
          CollapsibleTrigger,
          {
            className: "tw:flex tw:items-center tw:cursor-pointer tw:group",
            style: {
              gap: `${styles.headerGap}px`,
              marginBottom: `${styles.headerMarginBottom}px`
            },
            children: [
              /* @__PURE__ */ jsx(SearchIcon, { size: styles.headerIconSize, color: resolveColor(styles.headerIconColor) }),
              /* @__PURE__ */ jsx(
                "span",
                {
                  style: {
                    fontSize: `${styles.headerLabelFontSize}px`,
                    fontWeight: fontWeightMap[styles.headerLabelFontWeight] || 500,
                    fontFamily: styles.headerLabelFontFamily,
                    color: resolveColor(styles.headerLabelColor)
                  },
                  children: styles.headerLabelText
                }
              ),
              /* @__PURE__ */ jsx(
                ChevronDown,
                {
                  style: {
                    width: `${styles.headerChevronSize}px`,
                    height: `${styles.headerChevronSize}px`,
                    color: resolveColor(styles.headerChevronColor),
                    transition: `transform ${styles.chevronRotationDuration}ms`,
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
                  }
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(CollapsibleContent, { forceMount: true, className: cn(!isOpen && "tw:hidden"), children: /* @__PURE__ */ jsx(
          "div",
          {
            className: "tw:flex tw:flex-col tw:items-start",
            style: {
              gap: `${styles.pillsGap}px`,
              marginBottom: `${styles.pillsSectionMarginBottom}px`
            },
            children: previewSearchQueries.map((query, idx) => {
              const matchingQueryData = previewQueryProducts.find((qp) => qp.query === query);
              const isLoading = isStreaming && !matchingQueryData;
              const isAnimating = matchingQueryData ? animatedQueryIndices.has(matchingQueryData.queryIndex) : false;
              return /* @__PURE__ */ jsx(
                SearchQueryPill,
                {
                  query,
                  products: (matchingQueryData == null ? void 0 : matchingQueryData.products) ?? [],
                  totalResults: (matchingQueryData == null ? void 0 : matchingQueryData.totalResults) ?? 0,
                  isLoading,
                  isAnimating
                },
                `${query}-${idx}`
              );
            })
          }
        ) })
      ]
    }
  );
};
const ProductSearchQueriesSection = ({
  previewSearchQueries: previewSearchQueriesProp,
  previewQueryProducts,
  isStreaming,
  currentStatus,
  hasContent,
  isLastMessage = false
}) => {
  const previewSearchQueries = Array.isArray(previewSearchQueriesProp) ? previewSearchQueriesProp : [];
  if (previewSearchQueries.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Search Results", children: (styles, resolveColor) => /* @__PURE__ */ jsx("div", { style: { width: "100%" }, children: /* @__PURE__ */ jsx(
    ProductSearchQueriesSectionInner,
    {
      previewSearchQueries,
      previewQueryProducts,
      isStreaming,
      currentStatus,
      hasContent,
      isLastMessage,
      styles,
      resolveColor
    }
  ) }) });
};
export {
  ProductSearchQueriesSection
};
//# sourceMappingURL=ProductSearchQueriesSection.js.map
